import React, { useEffect, useMemo, useState } from "react";
import { FiClock, FiInfo, FiMapPin } from "react-icons/fi";
import { formatDrawSiteHours } from "../../services/drawSiteHours";
import "./DrawSiteSelector.css";

const sameSite = (left, right) => (
  left?.name === right?.name && left?.address === right?.address
);

export default function DrawSiteSelector({
  drawSites = [],
  pscCount = 0,
  zip,
  selectedSite,
  onSelect,
}) {
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    setShowAll(false);
  }, [drawSites, zip]);

  const orderedSites = useMemo(() => {
    if (!selectedSite) return drawSites;
    const selectedIndex = drawSites.findIndex((site) => sameSite(selectedSite, site));
    if (selectedIndex <= 0) return drawSites;
    return [drawSites[selectedIndex], ...drawSites.filter((_, index) => index !== selectedIndex)];
  }, [drawSites, selectedSite]);

  if (!drawSites.length) {
    if (pscCount <= 0) return null;
    return (
      <div className="lcw-info">
        <FiInfo size={14} />
        {" "}{pscCount} patient service center{pscCount !== 1 ? "s" : ""} found near {zip}. Details will be included in your order confirmation.
      </div>
    );
  }

  const visibleSites = showAll ? orderedSites : orderedSites.slice(0, 3);
  const hasMoreSites = orderedSites.length > 3;

  return (
    <div className="lcw-draw-sites">
      <div className="lcw-draw-sites-heading">
        <div>
          <div className="lcw-draw-sites-label">Choose a draw site</div>
          <div className="lcw-draw-sites-summary">
            {orderedSites.length} location{orderedSites.length !== 1 ? "s" : ""} near {zip}
          </div>
        </div>
      </div>
      {visibleSites.map((site, index) => {
        const selected = sameSite(selectedSite, site);
        const cityStateZip = [
          site.city,
          [site.state, site.zip].filter(Boolean).join(" "),
        ].filter(Boolean).join(", ");
        const addressLine = [site.address, cityStateZip].filter(Boolean).join(", ");
        return (
          <button
            key={`${site.name || "site"}-${site.address || index}`}
            type="button"
            className={`lcw-draw-card ${selected ? "selected" : ""}`}
            aria-pressed={selected}
            onClick={() => onSelect(site)}
          >
            <div className="lcw-method-radio">
              {selected && <div className="lcw-method-radio-dot" />}
            </div>
            <div className="lcw-draw-content">
              {site.provider_network && <div className="lcw-draw-network">{site.provider_network}</div>}
              <div className="lcw-draw-name">{site.name}</div>
              {addressLine && (
                <div className="lcw-draw-addr">
                  <FiMapPin aria-hidden="true" className="lcw-draw-location-icon" focusable="false" />
                  <span>{addressLine}</span>
                </div>
              )}
              {selected && site.hours && (
                <div className="lcw-draw-hours">
                  <FiClock size={11} />
                  <div className="lcw-draw-hours-list">
                    {formatDrawSiteHours(site.hours).map((schedule, scheduleIndex) => (
                      <div className="lcw-draw-hours-row" key={`${schedule.days}-${scheduleIndex}`}>
                        {schedule.days && <span className="lcw-draw-hours-day">{schedule.days}</span>}
                        <span>{schedule.hours}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {site.distance_miles != null && site.distance_miles !== "" && (
              <div className="lcw-draw-dist">{site.distance_miles} mi</div>
            )}
          </button>
        );
      })}
      {hasMoreSites && (
        <button
          type="button"
          className="lcw-draw-sites-toggle"
          aria-expanded={showAll}
          onClick={() => setShowAll((current) => !current)}
        >
          {showAll
            ? "Show fewer locations"
            : `Show all ${orderedSites.length} locations`}
        </button>
      )}
    </div>
  );
}
